import React, { useState, useRef, useEffect, ReactNode, useCallback } from 'react';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import { InputProps } from '@material-ui/core/Input';
import { Box } from '@material-ui/core';
import { Clear, Check } from '@material-ui/icons';
import { PasslyButton } from '@app/components';
import { useReadonlyTextFieldStylesV2, useTextFieldStylesV2 } from '@app/componentsV2/Forms/Select/theme';
import { TranslationsTypes } from '@app/models';
import { useTypedTranslation } from '@app/hooks';
import { HelpHint, Icon } from '@itglue/kaseya-ui';

interface ExtraTextFieldProps {
    inlineedit?: boolean;
    onClickSave?: Function;
    onClickDiscard?: Function;
    focusfunc?: Function;
    buttonWidth?: number;
    buttonFontSize?: 'large' | 'medium' | 'inherit' | 'small';
    hintText?: string;
    // if there is some error with the input, prevent save changes
    editdisabled?: boolean;
    endAdornment?: string | ReactNode;
    showSearchIcon?: boolean;
    readOnly?: boolean;
    onKeyDown?: (e: any) => void;
    onAddButtonClick?: (e: any) => void;
    onEditButtonClick?: (e: any) => void;
    onDeleteButtonClick?: (e: any) => void;
    customButton?: ReactNode;
    customWrapperClass?: string;
    requiredField?: boolean;
}

type PasslyTextFieldProps = TextFieldProps & ExtraTextFieldProps;

export const PasslyTextField: React.FC<PasslyTextFieldProps> = ({
    customWrapperClass,
    customButton,
    buttonWidth = 42,
    buttonFontSize = 'large',
    inlineedit,
    onClickSave,
    onClickDiscard,
    focusfunc,
    editdisabled,
    showSearchIcon,
    ...propsRest
}) => {
    const defaultClasses = useTextFieldStylesV2();
    const readonlyClasses = useReadonlyTextFieldStylesV2();
    const { placeholder, ...defaultClassesRest } = defaultClasses;

    const { translation } = useTypedTranslation();
    const [focused, setFocused] = useState<boolean>(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!focused && timerRef.current && inlineedit) {
            clearTimeout(timerRef.current);
        }
    }, [focused, inlineedit]);

    const handleKeyPress = useCallback(
        (e: React.KeyboardEvent) => {
            if (inlineedit) {
                if (e.keyCode === 27) { // Escape key
                    handleDiscard(e, propsRest.name);
                }
                if (e.keyCode === 13 && !editdisabled) { // Enter key
                    handleSave(e, propsRest.name);
                }
            }
        },
        [inlineedit, editdisabled, propsRest.name]
    );

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        setFocused(true);
        focusfunc && focusfunc(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        (timerRef.current = setTimeout(() => {
            handleDiscard(e, propsRest.name);
        }, 200));
    };

    const handleSave = useCallback(
        (e: any, name?: string) => {
            if (inlineedit && timerRef.current) {
                clearTimeout(timerRef.current);
                onClickSave?.(e, name);
                setFocused(false);
                handleBlur(e);
            }
        },
        [inlineedit, onClickSave]
    );

    const handleDiscard = useCallback(
        (e: React.MouseEvent | React.KeyboardEvent | React.FocusEvent, name?: string) => {
            if (inlineedit) {
                onClickDiscard?.(e, name);
                setFocused(false);
            }
        },
        [inlineedit, onClickDiscard]
    );

    const getEndAdornment = () => {
        if (propsRest.onAddButtonClick) {
            return <Icon fw regular className='text-field__icon' name={'fa-plus'} onClick={propsRest.onAddButtonClick} />;
        }

        return (
            <>
                {propsRest.onEditButtonClick && (
                    <Icon fw regular className='text-field__icon' name='fa-pencil' onClick={propsRest.onEditButtonClick} />
                )}
                {propsRest.onDeleteButtonClick && (
                    <Icon fw regular className='text-field__icon' name='fa-trash-alt' onClick={propsRest.onDeleteButtonClick} />
                )}
                {showSearchIcon && (
                    <i className='text-field__icon fa-regular fa-search' />
                )}
                {propsRest.endAdornment}
            </>
        );
    };

    return (
        <>
            {propsRest.label && (
                <span className={'text-field__label'}>
                    {translation((propsRest.label ?? '') as TranslationsTypes)}{' '}
                    {(propsRest.required || propsRest.requiredField) && <span className='text-field__label-asterisk'>*</span>}
                </span>
            )}
            <div className={`text-field__wrapper ${customWrapperClass || ''}`}>
                <div className='text-field__input-wrapper'>
                    <TextField
                        {...propsRest}
                        label=''
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyPress}
                        InputProps={
                            {
                                disableUnderline: true,
                                classes: propsRest.readOnly ? readonlyClasses : defaultClassesRest,
                                endAdornment: getEndAdornment(),
                                readOnly: propsRest.readOnly,
                                ...propsRest.InputProps
                            } as Partial<InputProps>
                        }
                        helperText={propsRest.helperText || undefined}
                    />
                    {propsRest.hintText && <HelpHint hintText={propsRest.hintText} />}
                </div>
                {inlineedit && focused && (
                    <div className='text-field__buttons-block'>
                        <Box className='text-field__button' ml={0.5} mb={1} width={buttonWidth} height={buttonWidth}>
                            <PasslyButton
                                style={{ width: buttonWidth, height: buttonWidth, padding: 0, minWidth: buttonWidth }}
                                name={propsRest.name}
                                variant='outlined'
                                onClick={handleBlur}
                            >
                                <Clear fontSize={buttonFontSize} name={propsRest.name} color='inherit' />
                            </PasslyButton>
                        </Box>
                        <Box className='text-field__button' ml={0.5} mb={1} width={buttonWidth} height={buttonWidth}>
                            <PasslyButton
                                style={{ width: buttonWidth, height: buttonWidth, padding: 0, minWidth: buttonWidth }}
                                disabled={editdisabled}
                                name={propsRest.name}
                                variant='contained'
                                color='primary'
                                onClick={(e: any) => handleSave(e, propsRest.name)}
                            >
                                <Check fontSize={buttonFontSize} name={propsRest.name} color='inherit' style={{ marginBottom: 2 }} />
                            </PasslyButton>
                        </Box>
                    </div>
                )}
                {customButton}
            </div>
        </>
    );
};
